import {
    TournamentMatchListActions,
    TournamentMatchListSetFilterDataAction,
    TournamentMatchListSetActiveMarketGroupAction,
    TournamentMatchListStoreModel,
    TournamentMatchListTopic,
    TournamentGroupsMatchListTopic,
    TournamentGroupsOverviewMatchListTopic,
} from './tournament-match-list.constants';
import { AppDispatch, AppThunk, GetState } from '@om-fe/core/core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import { 
    getTournamentMatchListSliceName,
    getTournamentGroupsMatchListSliceName,
    getTournamentMatchListTopic,
    getTournamentGroupsMatchListTopic,
    getTournamentGroupsOverviewMatchListTopic,
} from './tournament-match-list.utils';
import { 
    tournamentMatchListReducerCreator,
    tournamentGroupsMatchListReducerCreator,
    tournamentGroupsOverviewMatchListReducerCreator,
} from './tournament-match-list.reducer';
import { filterNullParams } from '@om-fe/core/utils/router';
import getSlice from '@om-fe/core/utils/get-slice';
import { DisciplineLiveStatus } from '@om-fe/models';

export const tournamentMatchListSetFilterData = (
    slice: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    groupsLimit: number | undefined,
    marketGroupsOverviewIds: string | undefined,
    extraParam: string | undefined,
    isAuthenForTopic: boolean | undefined,
): TournamentMatchListSetFilterDataAction => ({
    type: TournamentMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        tournamentId,
        liveStatus,
        isDefault,
        mainMarketsLimit,
        groupsLimit,
        marketGroupsOverviewIds,
        extraParam,
        isAuthenForTopic,
    },
});

export const tournamentMatchListSetActiveMarketGroup = (
    slice: string,
    activeMarketGroup: Record<string, string>,
): TournamentMatchListSetActiveMarketGroupAction => ({
    type: TournamentMatchListActions.SetActiveMarketGroup,
    meta: {
        slice,
    },
    payload: {
        activeMarketGroup,
    },
});

export const getTournamentMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    isAuthenForTopic: boolean | undefined,
) => (dispatch: AppDispatch) => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const topic: TournamentMatchListTopic = getTournamentMatchListTopic(
        tournamentId,
        liveStatus,
        isDefault,
        mainMarketsLimit,
        isAuthenForTopic,
    );

    dispatch(attachReducerAction(slice, tournamentMatchListReducerCreator(instanceId)));

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        mainMarketsLimit,
        undefined,
        undefined,
        undefined,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const getTournamentGroupsMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    groupsLimit: number,
    isAuthenForTopic: boolean | undefined,
) => (dispatch: AppDispatch) => {
    const slice: string = getTournamentGroupsMatchListSliceName(instanceId);
    const topic: TournamentGroupsMatchListTopic = getTournamentGroupsMatchListTopic(
        tournamentId,
        liveStatus,
        groupsLimit,
        isAuthenForTopic,
    );

    dispatch(attachReducerAction(slice, tournamentGroupsMatchListReducerCreator(instanceId)));

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        undefined,
        groupsLimit,
        undefined,
        undefined,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const getTournamentGroupsOverviewMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    marketGroupsOverviewIds: string,
    extraParam?: string,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch) => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const topic: TournamentGroupsOverviewMatchListTopic = getTournamentGroupsOverviewMatchListTopic(
        tournamentId,
        liveStatus,
        marketGroupsOverviewIds,
        isDefault,
        extraParam,
        isAuthenForTopic,
    );

    dispatch(attachReducerAction(slice, tournamentGroupsOverviewMatchListReducerCreator(instanceId)));

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        undefined,
        undefined,
        marketGroupsOverviewIds,
        extraParam,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeTournamentMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: TournamentMatchListTopic = getTournamentMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.isAuthenForTopic,
    );

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeTournamentGroupsMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getTournamentGroupsMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: TournamentGroupsMatchListTopic = getTournamentGroupsMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.groupsLimit!,
        matchListState.isAuthenForTopic!,
    );

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeTournamentGroupsOverviewMatchList = (
    instanceId: string,
    noDetach?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: TournamentGroupsOverviewMatchListTopic = getTournamentGroupsOverviewMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.marketGroupsOverviewIds!,
        matchListState.isDefault!,
        matchListState.extraParam!,
        matchListState.isAuthenForTopic!,
    );

    if (!noDetach) {
        dispatch(detachReducerAction(slice));
    }
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateTournamentMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    isAuthenForTopic: boolean | undefined,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: TournamentMatchListTopic = getTournamentMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.isAuthenForTopic,
    );

    const newTopic: TournamentMatchListTopic = getTournamentMatchListTopic(
        tournamentId,
        liveStatus,
        isDefault,
        mainMarketsLimit,
        isAuthenForTopic,
    );

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        mainMarketsLimit,
        undefined,
        undefined,
        undefined,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const updateTournamentGroupsMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    groupsLimit: number,
    isAuthenForTopic: boolean | undefined,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getTournamentGroupsMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: TournamentGroupsMatchListTopic = getTournamentGroupsMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.groupsLimit!,
        matchListState.isAuthenForTopic!,
    );

    const newTopic: TournamentGroupsMatchListTopic = getTournamentGroupsMatchListTopic(
        tournamentId,
        liveStatus,
        groupsLimit,
        isAuthenForTopic,
    );

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        undefined,
        groupsLimit,
        undefined,
        undefined,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const updateTournamentGroupsOverviewMatchList = (
    instanceId: string,
    tournamentId: string,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    marketGroupsOverviewIds: string,
    extraParam?: string,
    isAuthenForTopic?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getTournamentMatchListSliceName(instanceId);

    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: TournamentGroupsOverviewMatchListTopic = getTournamentGroupsOverviewMatchListTopic(
        matchListState.tournamentId!,
        matchListState.liveStatus!,
        matchListState.marketGroupsOverviewIds!,
        matchListState.isDefault!,
        matchListState.extraParam!,
        matchListState.isAuthenForTopic!,
    );

    const newTopic: TournamentGroupsOverviewMatchListTopic = getTournamentGroupsOverviewMatchListTopic(
        tournamentId,
        liveStatus,
        marketGroupsOverviewIds,
        isDefault,
        extraParam,
        isAuthenForTopic,
    );

    dispatch(tournamentMatchListSetFilterData(
        slice,
        tournamentId,
        liveStatus,
        isDefault,
        undefined,
        undefined,
        marketGroupsOverviewIds,
        extraParam,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const setActiveMarketGroup = (
    instanceId: string,
    matchId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getTournamentMatchListSliceName(instanceId);
    const matchListState: TournamentMatchListStoreModel | undefined = getSlice<TournamentMatchListStoreModel>(getState(), slice);

    let activeMarketGroup = {
        [matchId]: matchId,
    };

    if (matchListState?.activeMarketGroup && matchListState?.activeMarketGroup[matchId] === matchId) {
        activeMarketGroup = {};
    }

    dispatch(tournamentMatchListSetActiveMarketGroup(slice, activeMarketGroup));
}